// src/routes/ProtectedRoute.tsx

import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCookie } from '@/domains/auth/utils/cookies';
import ProtectedLayout from '@/components/AppComponents/Layout/ProtectedLayout';
import { CUSTOMER_TYPES, TUserCustomerType } from '@/shared/types/customer';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import { useUserCustomersStore } from '@/domains/user/userCustomers.store';
import { useUserProfileStore } from '@/domains/user/userProfile.store';
import {
  useUserProfileUseCase,
  useUserCustomersUseCase,
} from '@/domains/user/user.usecases';
import { useRouteAccess } from '@/shared/hooks/useRouteAccess';

const REVENUES_CUSTOMER_TYPES = new Set<TUserCustomerType>([
  CUSTOMER_TYPES.adtech,
  CUSTOMER_TYPES.ssp,
  CUSTOMER_TYPES.dsp,
  CUSTOMER_TYPES.creator,
]);

const COMMISSIONS_CUSTOMER_TYPES = new Set<TUserCustomerType>([
  CUSTOMER_TYPES.affiliate,
]);

const ProtectedRoute: React.FC = () => {
  const refreshToken = getCookie('refreshToken');
  const { pathname } = useLocation();

  const { isLoading: isProfileLoading } = useUserProfileUseCase({
    enabled: !!refreshToken,
  });
  const { isLoading: isCustomersLoading } = useUserCustomersUseCase({
    enabled: !!refreshToken,
  });

  const selectedCustomer = useUserCustomersStore((s) => s.selectedCustomer);
  const selectedCustomerUuid = useUserCustomersStore(
    (s) => s.selectedCustomerUuid,
  );
  const userProfile = useUserProfileStore((s) => s.user);

  const { canAccess, landingPath } = useRouteAccess(
    userProfile,
    selectedCustomer,
  );

  const isLoading: boolean = isProfileLoading || isCustomersLoading;

  const redirectTo = useMemo(() => {
    // without token -> login
    if (!refreshToken) return NAVBAR_SOURCES.login;
    // while loading, no redirect
    if (isLoading) return null;
    if (selectedCustomerUuid && !selectedCustomer) return null;
    // no customer (empty list): only /under-review
    if (!selectedCustomer) {
      if (pathname === '/' || canAccess(pathname)) return null;
      return landingPath;
    }
    const customerType = selectedCustomer?.type;
    const canAccessRevenues = customerType
      ? REVENUES_CUSTOMER_TYPES.has(customerType)
      : false;
    const canAccessCommissions = customerType
      ? COMMISSIONS_CUSTOMER_TYPES.has(customerType)
      : false;

    // cross type redirect
    if (pathname === NAVBAR_SOURCES.revenues && !canAccessRevenues) {
      return NAVBAR_SOURCES.commissions;
    }
    if (pathname === NAVBAR_SOURCES.commissions && !canAccessCommissions) {
      return NAVBAR_SOURCES.revenues;
    }
    // general access check (includes KYC block)
    if (pathname !== '/' && !canAccess(pathname)) {
      return landingPath;
    }

    return null;
  }, [
    refreshToken,
    isLoading,
    landingPath,
    pathname,
    canAccess,
    selectedCustomer,
    selectedCustomerUuid,
  ]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return <ProtectedLayout isRedirecting={isLoading} />;
};

export default ProtectedRoute;
